import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { setSessionKey, getExpirationTime } from '../../socket';
import { ENABLE_SEND_FILE } from '../../constants/global.constants';
import { ADD_EVENT, CLEAR_EVENTS } from '../../redux/reducers/eventReducer';
import getTimeStamp from '../../utils/getTimeStamp';
import { isInvalidFile } from '../../utils/uploadFileToS3';
import { SHOW_TOAST } from '../../redux/reducers/toastReducer';
import { generateRandomId } from '../../utils/global.utils';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import Ask from '../../assets/ask.svg';

type ChatUserInputSectionProps = {
  handelUserInput: (input: string) => void;
  handleSendAttachment?: (
    fileName: string,
    fileType: string,
    divId: string,
  ) => void;
};

const clientType = process.env.REACT_APP_CLIENT;

function ChatUserInputSection(props: ChatUserInputSectionProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [input, setInput] = useState('');
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const connectionStatus = useSelector(
    (state: RootState) => state.socketConnection.status,
  );
  const streaming = useSelector((state: RootState) => state.loader.streaming);

  // Check if session has expired
  const isSessionExpired = () => {
    const expirationTime = getExpirationTime();
    if (!expirationTime) return true; // No session, consider expired
    return new Date().getTime() > parseInt(expirationTime, 10);
  };

  // Check session on mount and when connection changes
  useEffect(() => {
    const hasValidSession = !isSessionExpired();
    setHasActiveSession(hasValidSession);
  }, [connectionStatus]);

  // Determine when to show "Start" button
  // Show Start only when session is expired
  const shouldShowStart = !hasActiveSession;

  // Determine when to show "Connecting..." button
  // Show Connecting when socket is connecting/disconnected but session is still valid
  const shouldShowConnecting =
    !shouldShowStart &&
    (connectionStatus === 'Connecting' || connectionStatus === 'Disconnected');

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = () => {
    if (input.trim() === '') return;
    props.handelUserInput(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const selectedFile = e.target.files?.[0];
    const isFileInvalid = isInvalidFile(selectedFile as File);

    if (!selectedFile || isFileInvalid) {
      dispatch({
        type: SHOW_TOAST,
        payload: { type: 'warning', message: isFileInvalid, interval: 3000 },
      });
      return;
    }

    try {
      const formData = new FormData();
      const userDetails = localStorage.getItem('userNumber') ?? '';
      formData.append('file', selectedFile);
      formData.append('fileName', userDetails);
      dispatch({
        type: SHOW_TOAST,
        payload: {
          type: 'info',
          message: 'Uploading your file',
          interval: 3000,
        },
      });
      const resp = await zenworksServiceApi.upload(formData);
      if (resp.url) {
        const timeStamp = getTimeStamp();
        const divId = generateRandomId();
        dispatch({
          type: ADD_EVENT,
          payload: {
            type: 'file',
            id: divId,
            divId,
            user: localStorage.getItem('userName'),
            time: timeStamp,
            senderType: 'USER',
            loading: false,
            fileType: selectedFile.type,
            fileName: selectedFile.name,
            file: selectedFile,
            msg: resp?.url, // file.fileData,
          },
        });
        if (props?.handleSendAttachment) {
          props.handleSendAttachment(resp?.url ?? '', selectedFile.type, divId);
        }
      } else {
        dispatch({
          type: SHOW_TOAST,
          payload: {
            type: 'error',
            message: 'Failed to upload file',
            interval: 3000,
          },
        });
      }
    } catch (e) {
      dispatch({
        type: SHOW_TOAST,
        payload: {
          type: 'error',
          message: 'Failed to upload file',
          interval: 3000,
        },
      });
    }
  };

  const handleSendFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const startNewSession = () => {
    dispatch({ type: CLEAR_EVENTS });
    localStorage.clear();
    setSessionKey();
    setHasActiveSession(true);
  };

  let inputPlaceholderValue = '';
  switch (clientType) {
    case 'salesbot2290ai':
      inputPlaceholderValue = 'Ask 2290 AI';
      break;
    case 'salesbottax1099':
      inputPlaceholderValue = 'Ask Zenwork AI';
      break;
    default:
      inputPlaceholderValue = 'Ask 2290 AI';
  }

  return (
    <div className="relative bg-[#F9F9F9]">
      <div className="flex items-center justify-around flex-nowrap sm:mb-0 mb-2">
        {shouldShowStart ? (
          <div className="w-full py-2 px-1">
            <button
              onClick={startNewSession}
              className={`w-full text-sm py-2 px-4 order-3 rounded-full ${clientType === 'salesbot2290ai'
                  ? 'bg-primary enabled:hover:bg-secondary'
                  : 'bg-[#114175] enabled:hover:bg-[#6491C3]'
                } text-white disabled:bg-gray-400 focus:outline-none`}
            >
              Start
            </button>
          </div>
        ) : shouldShowConnecting ? (
          <div className="w-full py-2 px-1">
            <button
              disabled
              className={`w-full text-sm py-2 px-4 order-3 rounded-full ${clientType === 'salesbot2290ai'
                  ? 'bg-primary enabled:hover:bg-secondary'
                  : 'bg-[#114175] enabled:hover:bg-[#6491C3]'
                } text-white disabled:bg-gray-400 focus:outline-none`}
            >
              Connecting...
            </button>
          </div>
        ) : (
          <>
            <div className="w-full pb-1 px-5">
              <div className="flex items-center border-[1px] border-[#D0D3D9] rounded-full bg-[#FFFFFF] px-2 w-full">
                {/* Attachment Icon */}
                {ENABLE_SEND_FILE && (
                  <div className="flex-shrink-0">
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      key={Date.now()}
                    />
                    <button
                      className="text-black hover:bg-gray-200 p-2 rounded-full"
                      type="button"
                      onClick={handleSendFile}
                    >
                      <IconAttachment width={18} height={18} />
                    </button>
                  </div>
                )}

                {/* Input Field */}
                <div className="flex-grow px-2">
                  <input
                    type="text"
                    placeholder={inputPlaceholderValue}
                    className="w-full bg-transparent outline-none text-gray-600 placeholder-gray-500"
                    value={input}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                {/* Ask Button */}
                <div className="flex-shrink-0">
                  <button
                    onClick={handleSubmit}
                    disabled={streaming}
                    className="text-black hover:bg-gray-200 p-2 rounded-full"
                  >
                    <img src={Ask} alt={'Ask'} className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChatUserInputSection;

function IconAttachment(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      height="0.5em"
      width="0.5em"
      {...props}
    >
      <path
        fill="grey"
        d="M14 0a5 5 0 015 5v12a7 7 0 11-14 0V9h2v8a5 5 0 0010 0V5a3 3 0 10-6 0v12a1 1 0 102 0V6h2v11a3 3 0 11-6 0V5a5 5 0 015-5z"
      />
    </svg>
  );
}
